import { Injectable } from "@angular/core";
import { createInterruptListenerObservable } from "@utils";
import { BehaviorSubject, pairwise, throttleTime, withLatestFrom } from "rxjs";
import { SettingsService } from "./settings.service";

const LAST_ACTIVE_AT_KEY = "last_active_at";

const SESSION_ID_STORAGE_KEY = "session_id";
const DEFAULT_SESSION_INACTIVITY_MILLISECONDS = 60000 * 30; // 30 minutes

@Injectable({
  providedIn: "root",
})
export class ActivityService {
  private lastActiveTime = new BehaviorSubject<number>(
    this.getLastActiveTime() ?? Date.now()
  );

  /** Epoch of the last active time in milliseconds (equivalent to `Date.now`) */
  public lastActiveTime$ = this.lastActiveTime.asObservable();

  constructor(private settingsService: SettingsService) {
    const sessionId = this.getSessionId();
    if (!sessionId) {
      this.generateAndStoreSessionId();
    }

    createInterruptListenerObservable()
      .pipe(throttleTime(500))
      .subscribe({
        next: () => {
          this.updateLastActiveTime();
        },
      });

    this.lastActiveTime$
      .pipe(pairwise(), withLatestFrom(this.settingsService.settings$))
      .subscribe({
        next: ([[prev, curr], settings]) => {
          const inactiveDurationMilliseconds = curr - prev;

          const inactivityMilliseconds = settings?.lockoutPeriod
            ? settings.lockoutPeriod * 60000
            : DEFAULT_SESSION_INACTIVITY_MILLISECONDS;

          if (inactiveDurationMilliseconds < inactivityMilliseconds) return;

          const _ = this.generateAndStoreSessionId();
        },
      });
  }

  private updateLastActiveTime() {
    const time = Date.now();
    localStorage.setItem(LAST_ACTIVE_AT_KEY, time.toString());
    this.lastActiveTime.next(time);
  }

  private getLastActiveTime(): number {
    const time = localStorage.getItem(LAST_ACTIVE_AT_KEY);
    if (!time) return null;
    return Number(time);
  }

  public generateAndStoreSessionId(): string {
    const sessionId = crypto.randomUUID();
    localStorage.setItem(SESSION_ID_STORAGE_KEY, sessionId);
    return sessionId;
  }

  public getSessionId(): string | null {
    let sessionId = localStorage.getItem(SESSION_ID_STORAGE_KEY);
    return sessionId ?? null;
  }
}
